import { useState, useCallback } from 'react';
import { updateDeployedAppMeta } from '../lib/deploy-api';

/** Minimal shape the editor needs from a deployed-app record. */
interface EditableApp {
  slug: string;
  name: string;
  description?: string | null;
  category?: string | null;
  team?: string | null;
}

interface UseDeployedAppMetaEditOptions {
  app: EditableApp | null;
  token: string;
  /** Refetch the app record after a successful save. */
  reload: () => Promise<void>;
  onSaved: (slug: string) => void;
  onError: (message: string) => void;
}

/**
 * Form state and save logic for the deployed-app metadata editor (name,
 * description, category, team, logo). Kept out of the page component so the
 * page stays a layout shell.
 */
export function useDeployedAppMetaEdit(opts: UseDeployedAppMetaEditOptions) {
  const { app, token, reload, onSaved, onError } = opts;

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [team, setTeam] = useState('');
  const [icon, setIcon] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const start = useCallback(() => {
    if (!app) return;
    setName(app.name);
    setDescription(app.description ?? '');
    setCategory(app.category ?? '');
    setTeam(app.team ?? '');
    setIcon(null);
    setEditing(true);
  }, [app]);

  const cancel = useCallback(() => setEditing(false), []);

  const save = useCallback(async () => {
    if (!app) return;
    setSaving(true);
    try {
      const ok = await updateDeployedAppMeta(token, app.slug, {
        name: name.trim() || app.name,
        description, category, team, icon,
      });
      if (!ok) throw new Error('Save failed');
      setEditing(false);
      onSaved(app.slug);
      await reload();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [app, token, name, description, category, team, icon, reload, onSaved, onError]);

  return {
    editing, saving,
    name, description, category, team, icon,
    setName, setDescription, setCategory, setTeam, setIcon,
    start, cancel, save,
  };
}
